import type {
  ActiveTransit,
  SadeSatiInfo,
  TransitPosition,
  TransitResponse,
} from "@ishvaram/api-client";
import { edgeRead } from "./_fetch";

export type { ActiveTransit, SadeSatiInfo, TransitPosition, TransitResponse };

/**
 * A date's transits for a Moon sign, read through the same-origin edge cache
 * (`/_jyotish/transits/current`, TTL 15 min in
 * app/api/jyotish-edge/[...slug]/route.ts). The reply depends only on
 * (moon_rashi, date), so the backend marks it shared-cacheable
 * (apps/backend/app/api/v1/transits.py). The sitewide header ribbon calls this
 * on every page view; reading the origin directly answered
 * `cf-cache-status: DYNAMIC` and ran Swiss Ephemeris on Cloud Run each time.
 *
 * `dateStr` (YYYY-MM-DD, normally today in IST) is required: the engine
 * answers one 06:00 snapshot per date, and a date-less read would keep serving
 * yesterday's answer under the same cache key after IST midnight. The query is
 * always `moon_rashi=…&date=…` in that order, so client callers asking for the
 * same (rashi, date) share one edge entry (the ribbon's Mesh/today read shares
 * MoonSignHouseCheck's and the Shani/Sade Sati default reads), and the cache
 * stays bounded at 12 rashis per date.
 *
 * Client-only. In the browser it is one edge attempt, plus one direct-origin
 * fallback only when the proxy itself fails; if the engine answers an error
 * through the edge, it throws rather than calling the engine again. On the
 * server, edgeRead skips the edge and wraps the read in withServerReadRetry
 * with no timeout here, so server code must not call this; give a server read
 * its own explicit timeout and attempt policy instead.
 */
export async function getCurrentTransits(
  moonRashi: string,
  dateStr: string
): Promise<TransitResponse> {
  const params = new URLSearchParams({ moon_rashi: moonRashi, date: dateStr });
  return edgeRead<TransitResponse>("transits/current", params.toString(), undefined, {
    fallbackOnUpstreamError: false,
  });
}
